/**
 * Bridge ↔ cloud connection (docs/wiring-documentation-proposal.md,
 * "Bridge ↔ cloud connection"): pairing key, SSE doorbells and the heartbeat
 * the bridge posts to the cloud.
 */
/** How often the bridge posts its status. */
export const BRIDGE_HEARTBEAT_INTERVAL_MS = 30_000;
/** A bridge without a heartbeat for this long is shown offline. */
export const BRIDGE_OFFLINE_AFTER_MS = 75_000;
/** The cloud sends an SSE ping this often; the bridge reconnects after
 *  BRIDGE_EVENTS_IDLE_TIMEOUT_MS without any data. */
export const BRIDGE_EVENTS_PING_INTERVAL_MS = 30_000;
export const BRIDGE_EVENTS_IDLE_TIMEOUT_MS = 75_000;
/** Prefix of generated pairing keys, so they are recognisable in configs. */
export const BRIDGE_KEY_PREFIX = "phb_";
/**
 * A usable bridge URL: http(s) with a host and nothing a shell or a URL
 * parser could read differently (whitespace, quotes, credentials).
 */
export function isBridgeUrl(value) {
    if (/[\s'"`\\$;|&<>(){}]/.test(value))
        return false;
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return false;
    }
    return ((url.protocol === "http:" || url.protocol === "https:") &&
        url.hostname !== "" &&
        url.username === "" &&
        url.password === "");
}
/** Online = a heartbeat within BRIDGE_OFFLINE_AFTER_MS. */
export function bridgeOnlineState(lastSeenAt, now = Date.now()) {
    if (lastSeenAt == null || lastSeenAt === "") {
        return { online: false, lastSeenAt: null };
    }
    const seen = typeof lastSeenAt === "number" ? lastSeenAt : Date.parse(lastSeenAt);
    if (!Number.isFinite(seen))
        return { online: false, lastSeenAt: null };
    return { online: now - seen <= BRIDGE_OFFLINE_AFTER_MS, lastSeenAt: seen };
}
/**
 * The studio's bridge state. Without a pairing it is "unpaired" whatever was
 * reported before; a heartbeat from before the current pairing (the previous
 * key's bridge) does not count. `now` should be the server's clock (see
 * `serverClockOffset`), since `lastSeenAt` is server time.
 */
export function bridgeConnectionState(input) {
    if (input.pairedAt == null)
        return { state: "unpaired", lastSeenAt: null };
    const { online, lastSeenAt } = bridgeOnlineState(input.lastSeenAt, input.now);
    if (lastSeenAt == null || lastSeenAt < input.pairedAt) {
        return { state: "waiting", lastSeenAt: null };
    }
    return { state: online ? "online" : "offline", lastSeenAt };
}
/**
 * Offset to add to the local clock to get the server's: the server read its
 * clock about halfway between sending the request and getting the answer.
 */
export function serverClockOffset(requestStartedAt, responseReceivedAt, serverNow) {
    return serverNow - (requestStartedAt + responseReceivedAt) / 2;
}
/**
 * Pairings migrated from the old `studios.bridge_api_key` have no key hint.
 * Those keys were synced to every employee's browser and may be short, so
 * admins should rotate them.
 */
export function isLegacyBridgePairing(input) {
    return input.pairedAt != null && !input.keyHint;
}
/**
 * Where the bridge's reported state differs from what the cloud expects,
 * e.g. after a bridge reset or a load that did not reach the cloud.
 */
export function bridgeStateWarnings(input) {
    const { status } = input;
    if (!status)
        return [];
    const warnings = [];
    if ((status.config_id ?? null) !== (input.activeConfigID ?? null)) {
        warnings.push(status.config_id
            ? "The bridge runs a different config than the active one"
            : "The bridge has no config loaded, but the studio has an active config");
    }
    else if (status.config_id &&
        status.version != null &&
        input.activeConfigVersion != null &&
        status.version !== input.activeConfigVersion) {
        warnings.push(`The bridge holds v${status.version}, the cloud has v${input.activeConfigVersion}`);
    }
    if (status.installation_version != null &&
        input.installationVersion != null &&
        status.installation_version < input.installationVersion) {
        warnings.push("The bridge has not picked up the latest wiring yet");
    }
    return warnings;
}
